import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress, Grid, TextField } from "@mui/material";
import { useAuth } from "../../Context/AuthProvider";
import { validateSecurityCusip } from "../../http/api";
import { useZeroPositionEntryStore } from "../../Store/zeroPositionEntryStore";
import { usePortfolioRebalanceStore } from "../../Store/portfolioRebalanceStore";
import { inputStyle } from "../OrderEntry/styles";
import useBondCalculations from "../../hooks/useBondCalculations";
import BondDetailsPanel from "../OrderEntry/BondDetailsPanel";
import { FloatInput } from "../OrderEntry/NumericInputs";
import { tokens } from "../../Common/designTokens";

const ZeroPositionEntryForm = ({ onValidationChange }) => {
  const { token, userId } = useAuth();
  const {
    cusip,
    price,
    qty,
    cusipError,
    bondDetails,
    setField,
    setCusipError,
    setBondDetails,
    setCalcResults,
    setCusipRef, // NEW: manage cusipRef in store
    cusipRef,    // NEW: read current cusipRef to avoid re-validation
    bondCalcResults: storeCalcResults,
  } = useZeroPositionEntryStore();

  const {
    bondCalcResults,
    calcLoading,
    calcErrorMessage,
    calculateBondValues,
  } = useBondCalculations({
    cusip,
    price,
    userId,
    token,
  });

  const { allPositionsForValidation, virtualZeroPosition } = usePortfolioRebalanceStore();

  const isUpdateMode = !!virtualZeroPosition;
  const [isLoading, setIsLoading] = useState(false);
  const [maturityMessage, setMaturityMessage] = useState(null);

  // Helper: maturity check
  const isSecMasterMatured = (secMasterOrData) => {
    if (!secMasterOrData) return false;
    const sec = secMasterOrData.secMasterDTO || secMasterOrData;
    const maturityStr =
      (sec && (sec.maturityDate || sec.maturity)) ||
      (secMasterOrData && secMasterOrData.maturity);
    if (!maturityStr) return false;
    const maturityDate = new Date(`${maturityStr}T00:00:00Z`);
    if (Number.isNaN(maturityDate.getTime())) return false;
    const todayUtcMidnight = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00Z");
    return maturityDate.getTime() < todayUtcMidnight.getTime();
  };

  // Push hook results into store
  useEffect(() => {
    setCalcResults(bondCalcResults || null);
  }, [bondCalcResults, setCalcResults]);

  // CUSIP validation effect
  useEffect(() => {
    const validateCusip = async () => {
      if (!cusip || isDuplicateCusip) return;
      // If we already have a cusipRef and it matches the typed cusip (case-insensitive),
      // use it as the canonical cusip and skip re-validating the same value.
      if (cusipRef !== '' && cusipRef !== null && cusipRef !== undefined) {
        if (cusipRef.toLowerCase() === String(cusip).toLowerCase()) {
          setField("cusip", cusipRef); // ensure cusip matches cusipRef
          return; // no need to re-validate same CUSIP
        }
      }
      if (cusip.length < 9) {
        setCusipError("Security must be at least 9 characters");
        setMaturityMessage(null);
        setBondDetails(null);
        setCalcResults(null);
        // clear cusipRef for invalid/too-short CUSIP
        setCusipRef('');
        return;
      }
      setIsLoading(true);
      setCusipError("");
      setMaturityMessage(null);
      try {
        const data = await validateSecurityCusip(token, cusip);
        if (data?.success === false) {
          setCusipError(data?.message || "Invalid Security");
          setBondDetails(null);
          setCalcResults(null);
          setMaturityMessage(null);
          setCusipRef('');
          return;
        }
        setBondDetails(data);
        // store cusipRef from API on success
        setCusipRef(data.cusip || '');
        // ensure local field uses API-normalized CUSIP
        setField("cusip", data.cusip || cusip);

        if (isSecMasterMatured(data)) {
          setMaturityMessage(
            "This bond has already matured (maturity date is before today). Bond calculations are not available."
          );
          setCalcResults(null);
          return;
        }

        // Always prefill price if backend provides it (removed previous price === "" condition)
        if (data.price !== null && data.price !== undefined && data.price !== "") {
          setField("price", `${data.price}`);
        }

      } catch (e) {
        console.error("Error validating Security:", e);
        setCusipError("Invalid Security or error fetching bond details");
        setBondDetails(null);
        setCalcResults(null);
        setMaturityMessage(null);
        // clear cusipRef on fetch/validation error
        setCusipRef('');
      } finally {
        setIsLoading(false);
      }
    };

    const t = setTimeout(() => {
      if (cusip) validateCusip();
    }, 500);

    return () => clearTimeout(t);
  }, [cusip]);

  // Debounced effect for bond calculations
  useEffect(() => {
    const triggerCalculation = async () => {
      const num = Number(price);
      if (!cusip || !bondDetails || !price || Number.isNaN(num) || num <= 0) return;

      if (isSecMasterMatured(bondDetails)) {
        setMaturityMessage(
          "This bond has already matured (maturity date is before today). Bond calculations are not available."
        );
        setCalcResults(null);
        return;
      }

      setMaturityMessage(null);
      try {
        if(!isDuplicateCusip) {
          await calculateBondValues(price,cusip);
        }
      } catch {
        /* calcError handled by hook */
      }
    };

    const debounceTimer = setTimeout(() => {
      if (price && cusip && bondDetails) {
        triggerCalculation();
      }
    }, 800);

    return () => clearTimeout(debounceTimer);
  }, [price, cusip, bondDetails, calculateBondValues, setCalcResults]);

  // Detect duplicate CUSIP in holdings
  const isDuplicateCusip = useMemo(() => {
    if (!cusip || !Array.isArray(allPositionsForValidation)) return false;
    const c = String(cusip).trim().toUpperCase();
    return allPositionsForValidation.some(
      (p) => !p?.isVirtualZeroPosition && String(p?.cusip || "").toUpperCase() === c
    );
  }, [cusip, allPositionsForValidation]);

  // On CUSIP change, flag error if already in holdings
  useEffect(() => {
    if (!cusip) return;
    if (isDuplicateCusip) {
      setCusipError(
        "This Security already exists"
      );
      setCalcResults(null);
      setBondDetails(null);
    }
  }, [cusip]);

  // NEW: Clear dependent data when CUSIP is cleared
  useEffect(() => {
    if (!cusip) {
      setBondDetails(null);
      setCalcResults(null);
      setCusipError("");
      setMaturityMessage(null);
      setCusipRef(''); // ensure cusipRef cleared when user clears CUSIP
    }
  }, [cusip, setBondDetails, setCalcResults, setCusipError]);

  // NEW: Clear calculations when price becomes empty/invalid so submit disables
  useEffect(() => {
    const n = Number(price);
    if (!price || !Number.isFinite(n) || n <= 0) {
      setCalcResults(null);
    }
  }, [price, setCalcResults]);

  // Validation state
  const isCusipValid = !!bondDetails && !cusipError && !!cusip;
  const isPriceValid = useMemo(() => {
    const n = Number(price);
    return !!price && Number.isFinite(n) && n > 0;
  }, [price]);
  // If price invalid we intentionally cleared storeCalcResults above
  const isCalcReady = !!storeCalcResults && !calcLoading && !calcErrorMessage && !maturityMessage;

  const canSubmit = isCusipValid && isPriceValid && isCalcReady && !isDuplicateCusip;

  // Notify parent of validation state changes
  useEffect(() => {
    onValidationChange?.({
      canSubmit,
      isDuplicateCusip,
      formData: canSubmit
        ? { cusip, price, bondDetails, storeCalcResults }
        : null, // prevent stale valid data when user clears fields
    });
  }, [canSubmit, isDuplicateCusip, cusip, price, bondDetails, storeCalcResults, onValidationChange]);

  const showBondDetailsSection =
    isLoading ||
    !!bondDetails ||
    !!storeCalcResults ||
    !!calcLoading ||
    !!calcErrorMessage ||
    !!cusipError ||
    !!maturityMessage;

  return (
    <>
      {/* Fields row with proper widths */}
      <Grid container spacing={2}>
        <Grid item xs={6}> {/* expanded from xs=4 to xs=6 */}
          <TextField
            label="SecurityId"
            value={cusip}
            onChange={(e) => setField("cusip", e.target.value)}
            variant="filled"
            placeholder="Enter SecurityId (e.g. 914302ER1)"
            InputProps={{
              disableUnderline: true,
              endAdornment: isLoading ? <CircularProgress size={20} /> : null,
            }}
            sx={inputStyle}
            error={!!cusipError}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}> {/* expanded from xs=4 to xs=6 */}
          <FloatInput
            name="price"
            label="Price"
            value={price}
            onChange={(e) => setField("price", e.target.value == null ? "" : e.target.value)}
            placeholder="Enter Price"
            disabled={false}
            error={!!calcErrorMessage}
            maxValue={150}
            InputProps={{
              disableUnderline: true,
              endAdornment: calcLoading ? <CircularProgress size={20} /> : null,
            }}
            sx={inputStyle}
          />
        </Grid>

        {/* REMOVED: Qty field - qty remains in store (default 0) and is still passed to BondDetailsPanel */}
      </Grid>

      {/* Bond details panel */}
      {showBondDetailsSection && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 1.5, color: tokens.colors.fontColour, fontSize: '1rem' }}
          >
            Bond Details
          </Typography>
          <BondDetailsPanel
            bondDetails={bondDetails}
            bondCalcResults={storeCalcResults}
            calcLoading={calcLoading}
            cusipError={cusipError}
            isLoading={isLoading}
            actualOrderData={{ cusip, qty, price }} // still passes qty (default 0)
            calcErrorMessage={calcErrorMessage || maturityMessage}
            isZeroPositionEntry={true}
          />
        </Box>
      )}
    </>
  );
};

export default ZeroPositionEntryForm;